import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import { tileIntersectsDataBounds } from './map-utils'

export interface TileLayer {
  id: string
  name: string
  url: string
  geoTiffUrl?: string
  visible: boolean
  opacity: number
  colormap?: string
  rescale?: string
  bounds?: L.LatLngBounds
}

interface MultipleTileServerLayersProps {
  layers: TileLayer[]
}

/**
 * Component to render multiple tile layers with bounds checking and auto-panning
 * Automatically pans to new layers when they're added
 */
export function MultipleTileServerLayers({ layers }: MultipleTileServerLayersProps) {
  const map = useMap()
  const layersRef = useRef<{ [key: string]: L.TileLayer }>({})
  const previousLayerIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    // Track new layers for auto-panning
    const currentLayerIds = new Set(layers.filter(l => l.visible).map(l => l.id))
    const newLayerIds = Array.from(currentLayerIds).filter(id => !previousLayerIdsRef.current.has(id))
    
    // Remove layers that are no longer in the list or not visible
    Object.keys(layersRef.current).forEach(layerId => {
      const layer = layers.find(l => l.id === layerId)
      if (!layer || !layer.visible) {
        if (layersRef.current[layerId]) {
          map.removeLayer(layersRef.current[layerId])
          delete layersRef.current[layerId]
        }
      }
    })

    // Add or update visible layers
    layers.forEach(layer => {
      if (layer.visible) {
        const isNewLayer = newLayerIds.includes(layer.id)
        
        if (layersRef.current[layer.id]) {
          // Update existing layer opacity
          layersRef.current[layer.id].setOpacity(layer.opacity)
        } else {
          // Create a custom tile layer that checks bounds before loading
          const CustomTileLayer = L.TileLayer.extend({
            getTileUrl: function (coords: any) {
              // If layer has bounds, check if tile intersects with data bounds
              if (layer.bounds && !tileIntersectsDataBounds(coords.z, coords.x, coords.y, layer.bounds)) {
                // Return transparent pixel for tiles outside data bounds
                console.log(`Tile ${coords.z}/${coords.x}/${coords.y} outside data bounds for ${layer.name}, skipping request`)
                return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=='
              }

              // Use the parent's getTileUrl method for tiles within bounds
              return L.TileLayer.prototype.getTileUrl.call(this, coords)
            }
          })

          const tileLayer = new (CustomTileLayer as any)(layer.url, {
            attribution: 'TiTiler',
            opacity: layer.opacity,
            maxZoom: 18,
            minZoom: 9,
            bounds: layer.bounds || L.latLngBounds(L.latLng(6, 68), L.latLng(38, 98)),
            errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
            keepBuffer: 1,
            updateWhenIdle: true,
            updateWhenZooming: false
          })

          // Add event listeners
          tileLayer.on('tileloadstart', (e: any) => {
            console.log(`Loading tile ${e.coords.z}/${e.coords.x}/${e.coords.y} for layer ${layer.name}`)
          })

          tileLayer.on('tileerror', (e: any) => {
            console.log(`Tile load error ${e.coords.z}/${e.coords.x}/${e.coords.y}:`, e.error)
          })

          layersRef.current[layer.id] = tileLayer.addTo(map)
          
          // Auto-pan to new layer bounds if this is a newly added layer
          if (isNewLayer && layer.bounds) {
            console.log(`Auto-panning to new layer: ${layer.name}`, layer.bounds)
            map.fitBounds(layer.bounds, {
              padding: [50, 50],
              maxZoom: 15,
              animate: true,
              duration: 0.5
            })
          }
        }
      }
    })
    
    // Update previous layer IDs for next render
    previousLayerIdsRef.current = currentLayerIds

    return () => {
      // Cleanup all layers on unmount
      Object.values(layersRef.current).forEach(layer => {
        map.removeLayer(layer)
      })
      layersRef.current = {}
    }
  }, [map, layers])

  return null
}
